/**
 * Addons.
 *
 * People wanted to extend Pugo. The honest problem with that is that an
 * extension is somebody else's code running inside your session, and your
 * session can read your messages, act as you, and reach the server with your
 * token. So the design question is not "how do we run their code" - that part
 * is easy - it is "how do we run it such that the worst addon on the internet
 * is still harmless".
 *
 * The answer here is that an addon never runs in the app at all. It runs in a
 * sandboxed iframe with no origin, which means:
 *
 *   - it cannot read or write localStorage, so it cannot reach the token
 *   - it cannot touch the app's DOM, so it cannot read your messages or
 *     rewrite a button to do something else
 *   - it cannot open a network connection of any kind, because its own
 *     Content-Security-Policy forbids every one
 *   - it cannot call the Pugo API, because it has no token and no network
 *
 * What it *can* do is draw inside its own small panel and ask the host for a
 * short list of harmless facts. Everything it receives is passed deliberately,
 * one message at a time, by the code below. Nothing is exposed by accident,
 * because nothing is exposed by default.
 *
 * The consequence, stated plainly: addons are widgets, not plugins. They
 * cannot change how Pugo behaves. That is the price of them being safe to
 * install without reading the source first, and it is the right trade.
 */

const STORE_KEY = 'pugo.addons';
const DATA_KEY = 'pugo.addondata';

export type Addon = {
  id: string;
  name: string;
  author: string;
  description: string;
  code: string;
  enabled: boolean;
  addedAt: number;
};

/* --------------------------------------------------------------- store */

function read<T>(key: string, fallback: T): T {
  try {
    const raw = localStorage.getItem(key);
    return raw ? (JSON.parse(raw) as T) : fallback;
  } catch {
    return fallback;
  }
}

function write(key: string, value: unknown): void {
  try { localStorage.setItem(key, JSON.stringify(value)); } catch { /* full or blocked */ }
}

export function listAddons(): Addon[] {
  return read<Addon[]>(STORE_KEY, []);
}

export function saveAddons(list: Addon[]): void {
  write(STORE_KEY, list);
  listeners.forEach((fn) => fn());
}

const listeners = new Set<() => void>();
export function onAddonsChanged(fn: () => void): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}

/**
 * Whether something is plausibly an addon rather than a paste accident.
 *
 * Deliberately not a security check - the sandbox is the security, and a
 * scanner that looked for "dangerous" strings would only teach people to
 * obfuscate them. This is here to catch mistakes, not attackers.
 */
export function validateAddon(name: string, code: string): string | null {
  if (!name.trim()) return 'Give it a name.';
  if (name.length > 40) return 'That name is too long.';
  if (!code.trim()) return 'There is no code.';
  if (code.length > 200_000) return 'That is larger than an addon should be.';
  return null;
}

export function addAddon(input: { name: string; author?: string; description?: string; code: string }): Addon {
  const addon: Addon = {
    id: 'a_' + Math.random().toString(36).slice(2, 10),
    name: input.name.trim().slice(0, 40),
    author: (input.author || 'Unknown').trim().slice(0, 40),
    description: (input.description || '').trim().slice(0, 200),
    code: input.code,
    enabled: true,
    addedAt: Date.now()
  };
  saveAddons([...listAddons(), addon]);
  return addon;
}

export function removeAddon(id: string): void {
  saveAddons(listAddons().filter((a) => a.id !== id));
  // Its saved state goes with it; a removed addon should leave nothing behind.
  const all = read<Record<string, unknown>>(DATA_KEY, {});
  delete all[id];
  write(DATA_KEY, all);
}

export function setEnabled(id: string, enabled: boolean): void {
  saveAddons(listAddons().map((a) => (a.id === id ? { ...a, enabled } : a)));
}

/* ------------------------------------------------- per-addon storage */

/*
 * An addon cannot use localStorage itself - it has no origin. So the host
 * keeps a small box for each one, namespaced by id, and hands it over on
 * request. One addon cannot read another's box because the id comes from the
 * frame it arrived on, not from anything the addon said.
 */
export function addonData(id: string): Record<string, unknown> {
  const all = read<Record<string, Record<string, unknown>>>(DATA_KEY, {});
  return all[id] || {};
}

export function setAddonData(id: string, data: Record<string, unknown>): void {
  const all = read<Record<string, Record<string, unknown>>>(DATA_KEY, {});
  all[id] = data;
  write(DATA_KEY, all);
}

/* ------------------------------------------------------- the sandbox */

/**
 * The page an addon actually runs inside.
 *
 * Everything about this is deliberate. The CSP allows inline script - the
 * addon is inline - and nothing else at all: no connect-src, so fetch and
 * WebSocket fail; no img-src beyond data:, so it cannot beacon out by setting
 * an image source; no frame-src, so it cannot nest its way out.
 *
 * `pugo` is the entire surface an addon gets. Anything not on it does not
 * exist, and the host validates every request that comes back regardless.
 */
export function sandboxHtml(addon: Addon, tokens: string): string {
  return `<!doctype html><html><head><meta charset="utf-8">
<meta http-equiv="Content-Security-Policy" content="default-src 'none'; script-src 'unsafe-inline'; style-src 'unsafe-inline'; img-src data:; font-src data:;">
<style>
  /* tokens carries color-scheme as well as the colours - see Addons.tsx.
     Without it the transparent body below falls through to the browser's
     own canvas, which is white, and every addon sat in a white box. */
  :root { ${tokens} }
  * { box-sizing: border-box; }
  /* Scrolling inside a capped panel stops at its ends instead of dragging the page along. */
  html { overscroll-behavior: contain; scrollbar-width: thin; }
  html, body { margin: 0; padding: 0; background: transparent; color: var(--text, #f4f6fa);
    font: 13px/1.6 ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif; }
  body { padding: 12px; }
  a { color: var(--accent-hi, #7568ff); }
  button { font: inherit; color: inherit; cursor: pointer;
    background: var(--bg-3, #1b2434); border: 1px solid rgba(255,255,255,.11);
    border-radius: 8px; padding: 6px 12px; }
  button:hover { background: var(--bg-4, #253044); }
</style></head><body>
<div id="root"></div>
<script>
(function () {
  var pending = {}, seq = 0;

  function ask(kind, payload) {
    return new Promise(function (resolve) {
      var id = ++seq;
      pending[id] = resolve;
      parent.postMessage({ pugo: true, id: id, kind: kind, payload: payload }, '*');
    });
  }

  window.addEventListener('message', function (e) {
    var d = e.data;
    if (!d || !d.pugo) return;
    if (d.id && pending[d.id]) { pending[d.id](d.result); delete pending[d.id]; return; }
    if (d.kind === 'event' && window.pugo && window.pugo._on[d.name]) {
      window.pugo._on[d.name].forEach(function (fn) { try { fn(d.payload); } catch (err) {} });
    }
  });

  window.pugo = {
    _on: {},
    /** The panel this addon draws into. */
    root: document.getElementById('root'),
    /** Public facts about you. No address, no token, nothing private. */
    me: function () { return ask('me'); },
    /** Which screen the app is on, and when that changes. */
    route: function () { return ask('route'); },
    /** A small box of your own. Survives a restart; nobody else can read it. */
    storage: {
      get: function () { return ask('storage.get'); },
      set: function (data) { return ask('storage.set', data); }
    },
    /** What to call this panel. */
    setTitle: function (text) { return ask('title', String(text || '')); },
    /** How tall the panel should be, in pixels. */
    setHeight: function (px) { return ask('height', Number(px) || 0); },
    /** A short message in the app's toast. Rate-limited by the host. */
    notify: function (text) { return ask('notify', String(text || '')); },
    /**
     * The party this panel is running in, when it runs in one.
     *
     * get() resolves null on Home. In a party it is { partyId, key, gameId,
     * hostId, meId, canEdit, members, shared, players }, and a 'party' event
     * fires with the same shape whenever anything in it changes - somebody
     * writes, joins or leaves. shared is written by the owner and officers,
     * each player's own box by that player.
     */
    party: {
      get: function () { return ask('party.get'); },
      setShared: function (data) { return ask('party.setShared', data); },
      setMine: function (data) { return ask('party.setMine', data); }
    },
    /** Puts text on your clipboard - the host does the copying, after a click. */
    copy: function (text) { return ask('copy', String(text || '')); },
    /** The app's current colours, so the panel can match the theme. */
    theme: function () { return ask('theme'); },
    on: function (name, fn) {
      (this._on[name] = this._on[name] || []).push(fn);
    }
  };

  try {
${addon.code}
  } catch (err) {
    document.getElementById('root').textContent = 'This addon stopped: ' + err.message;
  }
})();
</script></body></html>`;
}

/**
 * An example, so the settings screen has something to show rather than an
 * empty box and a file picker.
 */
export const SAMPLE_ADDON = `// A tiny addon. It counts your sessions and greets you.
pugo.setTitle('Hello');

pugo.me().then(function (me) {
  pugo.storage.get().then(function (saved) {
    var visits = (saved.visits || 0) + 1;
    pugo.storage.set({ visits: visits });

    pugo.root.innerHTML =
      '<div style="font-weight:700;font-size:15px">Hello, ' + me.displayName + '</div>' +
      '<div style="opacity:.7;margin-top:4px">Level ' + me.level +
      ' &middot; you have opened this ' + visits + ' times</div>';

    pugo.setHeight(pugo.root.scrollHeight + 24);
  });
});`;
